import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { actNumber, type ActId } from "../acts";

/**
 * A casca de um bloco do Resumo (EF-DIAG-004): número, nome, título, o link para
 * o ato de origem e, no fim, a frase de leitura.
 *
 * O link é um botão que troca a aba, como o rodapé dos atos: o ato vive em
 * memória e não na URL (#852). Sem frase de leitura, o parágrafo não aparece, em
 * vez de dizer algo sobre número que não existe. A frase fica no fim do card
 * (`mt-auto`), para os cards da mesma linha terminarem alinhados.
 */
export function SummaryBlock({
  id,
  index,
  overline,
  title,
  act,
  onGoToAct,
  reading,
  children,
}: {
  id: string;
  index: number;
  overline: string;
  title: string;
  act: ActId;
  onGoToAct: (act: ActId) => void;
  reading?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <section
      data-testid={`summary-block-${id}`}
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-[#e8ebf0] bg-white p-5"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span
          aria-hidden
          className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-full bg-[#ffc83d] text-[14px] font-extrabold text-[#08254c]"
        >
          {index}
        </span>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#7b8799]">
            {overline}
          </div>
          <h3 className="text-[17px] font-extrabold leading-tight text-[#08254c]">{title}</h3>
        </div>
        <button
          type="button"
          onClick={() => onGoToAct(act)}
          className="ml-auto inline-flex flex-none items-center gap-1 text-[12px] font-semibold text-[#1d4ea0] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
        >
          {t("See act {{n}}", { n: actNumber(act) })}
          <ArrowRight aria-hidden className="h-3 w-3" />
        </button>
      </div>

      {children}

      {reading && (
        <p
          data-testid={`summary-reading-${id}`}
          className="m-0 mt-auto rounded-md bg-[#f2f5f9] px-3 py-2 text-[13px] leading-snug text-[#2c3e5b]"
        >
          {reading}
        </p>
      )}
    </section>
  );
}

/** Um número do bloco: valor grande, legenda pequena. Sem fonte, o valor é o travessão. */
export function SummaryStat({
  testId,
  value,
  label,
  hint,
  compact = false,
}: {
  testId: string;
  value: string;
  label: string;
  hint?: string;
  /** Para valores em reais por extenso, que não cabem em três colunas no tamanho cheio. */
  compact?: boolean;
}) {
  return (
    <div data-testid={testId} className="min-w-0">
      <b
        className={`block break-words font-extrabold leading-tight tabular-nums text-[#08254c] ${compact ? "text-[19px]" : "text-[24px]"}`}
      >
        {value}
      </b>
      <small className="block text-[11.5px] leading-snug text-[#5a6b84]">{label}</small>
      {hint && <small className="block text-[11px] leading-snug text-[#7b8799]">{hint}</small>}
    </div>
  );
}
